// packages/shared-hooks/src/engagements/useEngagementsList.ts
//
// `GET /engagements` (#2257 FR-1 / #2247 FR-1) — the manager-facing v4 funnel
// list over the READ-ONLY Engagement projection: bucket selector + server
// counts + score/client/search filters + sort + cursor pagination. READ-ONLY:
// the projection is refreshed by the legacy Lead bridge (M7); this hook never
// mutates. `useInfiniteQuery` threads the opaque `nextCursor` straight back as
// `cursor` (never parsed). Keyed on the full filter shape so switching a bucket
// / score / client / search / sort spawns a fresh cursor walk (mirrors
// `useLeadsInfinite`).
import { useInfiniteQuery, type InfiniteData } from '@tanstack/react-query';
import {
  EngagementListViewSchema,
  type EngagementListView,
  type GetEngagementsQuery,
} from '@magimp/shared';
import { useApiClient } from '../api/context.js';
import { CRM_V4_FIDELITY_HEADER, CRM_V4_SCOPE_UI_HEADER, engagementsKeys } from './keys.js';

/**
 * Filter shape for {@link useEngagementsList} — the documented `GET /engagements`
 * query keys minus `cursor` (threaded through `pageParam`). `bucket`/`sort`
 * default server-side; omit a key to leave it unset.
 * @aiInvariant `GetEngagementsQuerySchema` is `.strict()` — only these keys may
 *   reach the wire. `toQuery` drops `undefined`, so an unset key is never sent.
 */
export type EngagementsListFilter = Omit<Partial<GetEngagementsQuery>, 'cursor'>;

function toQuery(
  filter: EngagementsListFilter,
  cursor: string | undefined,
): Record<string, string | number | undefined> {
  return {
    bucket: filter.bucket,
    phase: filter.phase,
    score: filter.score,
    clientId: filter.clientId,
    q: filter.q,
    sort: filter.sort,
    limit: filter.limit,
    cursor,
  };
}

/**
 * @param options.fidelity OPT-IN (#2304): send the `X-Magimp-Crm-V4-Fidelity`
 *   header so the response carries the additive fidelity fields
 *   (`source`/`hasOpenTask`/`openTaskDueAt`/…), and key under a DISTINCT cache
 *   slot so the wider shape never shares an entry with the default read.
 * @param options.scope OPT-IN (#2641): send the `X-Magimp-Crm-V4-Scope-Ui`
 *   header so all-scope users receive the unassigned count and assignee names.
 *   This also owns a distinct cache slot so a permission downgrade cannot
 *   render scope-only response data from the previous session capability.
 */
export function useEngagementsList(
  filter: EngagementsListFilter = {},
  options?: { fidelity?: boolean; scope?: boolean },
) {
  const client = useApiClient();
  const fidelity = options?.fidelity === true;
  const scope = options?.scope === true;
  return useInfiniteQuery<
    EngagementListView,
    Error,
    InfiniteData<EngagementListView>,
    readonly unknown[],
    string | undefined
  >({
    queryKey: engagementsKeys.list(filter as Record<string, unknown>, { fidelity, scope }),
    initialPageParam: undefined,
    queryFn: ({ pageParam, signal }) =>
      client.get<EngagementListView>(
        '/engagements',
        {
          signal,
          query: toQuery(filter, pageParam),
          ...(fidelity || scope
            ? {
                headers: {
                  ...(fidelity ? { [CRM_V4_FIDELITY_HEADER]: '1' } : {}),
                  ...(scope ? { [CRM_V4_SCOPE_UI_HEADER]: '1' } : {}),
                },
              }
            : {}),
        },
        EngagementListViewSchema,
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}
